import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { BackButton } from '@/components/shared/BackButton';
import {
  fetchLabels,
  createLabel,
  updateLabel,
  deleteLabel,
  type EmailLabel,
} from '@/lib/inboxLabelsApi';
import { LabelChip } from '@/components/inbox/LabelChip';

/**
 * Ten label colours, ordered around the spectrum with the neutral last.
 *
 * Chosen by measurement, not by eye, because a label colour has exactly two jobs: be told apart
 * from the other nine, and stay readable. `LabelChip` paints the chip's TEXT in this colour (over
 * a 13% tint of itself), so a colour that is too pale or too dark stops being readable in one of
 * the two themes.
 *
 * What was here before: `#374151` (slate). It sat 20.8 ΔE from `#6b7280` — by some distance the
 * closest pair in the set, two greys doing one job — and scored 1.74 contrast on the dark card,
 * i.e. all but invisible in dark mode. `#8f7e3d` replaces it: 45.3 ΔE from its nearest neighbour
 * and 4.02 / 4.45 contrast on the light and dark cards, the only candidate tested that clears 4.0
 * in both. `back-control`-style guard: `inbox-label-palette.guard.test.ts`.
 *
 * `#6b7280` stays: it is the server's default when a label is created without one
 * (`inbox.labels.service.ts:50`, `schema.prisma:4898`), so dropping it would leave that default
 * pointing at a colour the picker no longer offers.
 */
const PRESET_COLOURS = [
  '#2563EB', '#0891b2', '#059669', '#65a30d',
  '#8f7e3d', '#d97706', '#dc2626', '#db2777',
  '#9333ea', '#6b7280',
];

interface LabelFormState {
  name: string;
  colour: string;
}

const EMPTY_FORM: LabelFormState = { name: '', colour: '#2563EB' };

export default function InboxLabelsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<LabelFormState>(EMPTY_FORM);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data: labels = [], isLoading } = useQuery({
    queryKey: ['inbox-labels'],
    queryFn: fetchLabels,
    // Cross-device freshness. `invalidateQueries` only refreshes the device that made the
    // change, and the inbox module emits no socket events, so a second device sat on its cached
    // list until the person refreshed by hand (seen: a signature added on a phone never appeared
    // on the open desktop tab). Polling only runs while the tab is visible — react-query's
    // `refetchIntervalInBackground` defaults to false — so an idle background tab costs nothing.
    // 15s, not 60: a minute is long enough that someone adding a signature on a phone and
    // turning to the desktop reads it as broken and refreshes by hand anyway.
    refetchInterval: 15_000,
    // Global staleTime is 60s (main.tsx:57), which would make a focus refetch a no-op for
    // the first minute. These lists are small and change rarely — always refetch.
    staleTime: 0,
  });

  const createMutation = useMutation({
    mutationFn: (input: LabelFormState) => createLabel(input),
    onSuccess: () => {
      toast.success('Label created');
      void queryClient.invalidateQueries({ queryKey: ['inbox-labels'] });
      setShowForm(false);
      setForm(EMPTY_FORM);
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to create label'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: LabelFormState }) =>
      updateLabel(id, input),
    onSuccess: () => {
      toast.success('Label updated');
      void queryClient.invalidateQueries({ queryKey: ['inbox-labels'] });
      setEditingId(null);
      setForm(EMPTY_FORM);
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to update label'),
  });

  const deleteMutation = useMutation({
    mutationFn: (labelId: string) => deleteLabel(labelId),
    onSuccess: () => {
      toast.success('Label deleted');
      void queryClient.invalidateQueries({ queryKey: ['inbox-labels'] });
      setDeletingId(null);
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to delete label'),
  });

  function startEdit(label: EmailLabel) {
    setEditingId(label.id);
    setForm({ name: label.name, colour: label.colour });
    setShowForm(false);
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  function handleSubmit() {
    if (!form.name.trim()) {
      toast.error('Label name is required');
      return;
    }
    if (editingId) {
      updateMutation.mutate({ id: editingId, input: form });
    } else {
      createMutation.mutate(form);
    }
  }

  return (
    <div style={{
      maxWidth: 680,
      margin: '0 auto',
      padding: 'clamp(20px, 5vw, 32px) clamp(14px, 4vw, 24px)',
      fontFamily: "'IBM Plex Sans', sans-serif",
    }}>
      {/* Was an unlabelled arrow wired to a fixed destination. These pages are opened from the
          inbox, from the Email settings hub and from Settings, so a fixed target sent people
          somewhere they had never been. Back means back — same control as every other page. */}
      <BackButton onClick={() => navigate(-1)} />

      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
            Labels
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '2px 0 0' }}>
            Organise your email with custom labels
          </p>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditingId(null); setForm(EMPTY_FORM); }}
          style={{
            marginLeft: 'auto',
            background: '#1e3a5f', color: 'white',
            border: 'none', borderRadius: 6,
            padding: '8px 14px', fontSize: 13, fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          + New label
        </button>
      </div>

      {/* Create / edit form */}
      {(showForm || editingId) && (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border-default)',
          borderRadius: 8, padding: 20, marginBottom: 24,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>
            {editingId ? 'Edit label' : 'New label'}
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Name field */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                Name
              </label>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Urgent, Billing, Court"
                maxLength={50}
                autoFocus
                style={{
                  width: '100%', padding: '10px 12px',
                  border: '1px solid var(--border-strong)', borderRadius: 6,
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 16, color: 'var(--text-primary)',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Colour picker */}
            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                Colour
              </label>
              {/* The swatches were wrapping onto a second line because of a 224px cap on this
                  row — room for seven — while the field beside them sat half empty. Full size,
                  full width, tighter gap: ten 24px swatches plus 4px gaps is 276px, which fits
                  the card on a phone. They keep their size and wrap only if a screen is
                  genuinely too narrow for the row; they never shrink. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {PRESET_COLOURS.map((colour) => (
                  <button
                    key={colour}
                    onClick={() => setForm((f) => ({ ...f, colour }))}
                    aria-label={`Use colour ${colour}`}
                    aria-pressed={form.colour === colour}
                    style={{
                      width: 24, height: 24, flexShrink: 0, borderRadius: '50%',
                      background: colour,
                      border: form.colour === colour ? '2px solid #111827' : '2px solid transparent',
                      cursor: 'pointer',
                      outline: form.colour === colour ? '1px solid white' : 'none',
                      outlineOffset: -3,
                    }}
                    title={colour}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Preview */}
          {form.name.trim() && (
            <div style={{ marginTop: 14 }}>
              <span style={{ fontSize: 12, color: 'var(--text-subtle)', marginRight: 8 }}>Preview:</span>
              <LabelChip label={{ name: form.name, colour: form.colour }} size="md" />
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
            <button
              onClick={handleSubmit} className="btn-mobile-full"
              disabled={createMutation.isPending || updateMutation.isPending}
              style={{
                background: '#1e3a5f', color: 'white', border: 'none',
                borderRadius: 6, flex: 1, minWidth: 150, padding: '11px 16px', fontSize: 14, fontWeight: 500,
                cursor: 'pointer', opacity: (createMutation.isPending || updateMutation.isPending) ? 0.6 : 1,
              }}
            >
              {editingId ? 'Save changes' : 'Create label'}
            </button>
            <button
              onClick={() => { setShowForm(false); cancelEdit(); }} className="btn-mobile-full"
              style={{
                background: 'var(--surface-card)', color: 'var(--text-primary)',
                border: '1px solid var(--border-strong)', borderRadius: 6,
                flex: 1, minWidth: 150, padding: '11px 16px', fontSize: 14, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Labels list */}
      {isLoading ? (
        <div style={{ color: 'var(--text-subtle)', fontSize: 13 }}>Loading labels…</div>
      ) : labels.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '48px 24px',
          color: 'var(--text-subtle)', fontSize: 13,
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🏷</div>
          <div style={{ fontWeight: 500, color: 'var(--text-muted)', marginBottom: 6 }}>No labels yet</div>
          <div>Create labels to organise and find emails quickly.</div>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border-default)', borderRadius: 8, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border-default)' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>
                  Label
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>
                  Emails
                </th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {labels.map((label, i) => (
                <tr
                  key={label.id}
                  style={{
                    borderBottom: i < labels.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                    background: editingId === label.id ? '#eff6ff' : 'white',
                  }}
                >
                  <td style={{ padding: '12px 16px' }}>
                    <LabelChip label={label} size="md" />
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, color: 'var(--text-muted)' }}>
                    {label.emailCount}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => startEdit(label)}
                        style={{
                          background: 'none', border: 'none',
                          cursor: 'pointer', color: 'var(--text-muted)',
                          fontSize: 12, fontFamily: "'IBM Plex Sans', sans-serif",
                          padding: '8px 12px', borderRadius: 4,
                        }}
                      >
                        Edit
                      </button>
                      {deletingId === label.id ? (
                        <>
                          <span style={{ fontSize: 12, color: '#dc2626', alignSelf: 'center' }}>
                            Delete?
                          </span>
                          <button
                            onClick={() => deleteMutation.mutate(label.id)}
                            disabled={deleteMutation.isPending}
                            style={{
                              background: '#dc2626', color: 'white',
                              border: 'none', borderRadius: 4,
                              padding: '8px 12px', fontSize: 12, cursor: 'pointer',
                            }}
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setDeletingId(null)}
                            style={{
                              background: 'none', border: '1px solid var(--border-strong)',
                              borderRadius: 4, padding: '8px 12px', fontSize: 12, cursor: 'pointer',
                              color: 'var(--text-primary)',
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setDeletingId(label.id)}
                          style={{
                            background: 'none', border: 'none',
                            cursor: 'pointer', color: 'var(--text-subtle)',
                            fontSize: 12, fontFamily: "'IBM Plex Sans', sans-serif",
                            padding: '8px 12px', borderRadius: 4,
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
